<template>
  <div>
    <div class="level table-actions mt-20">
      <div class="level-left">
        <form @submit.prevent="onSearch">
          <div class="field is-grouped controls">
            <div class="control">
              <b-input v-model="filters.q" :placeholder="$t('interface.search')"></b-input>
            </div>
            <div class="control mr-10">
              <button type="submit" class="button is-primary">
                <b-icon icon="magnify" size="is-small"></b-icon>
              </button>
            </div>
          </div>
        </form>
      </div>
      <div class="level-right">
        <div class="control mt-5">
          <a @click="showFilters = !showFilters">
            <b-icon icon="filter-outline" size="is-small"></b-icon>
            {{ $t('interface.advancedSearch') }}
            <b-icon icon="chevron-down" size="is-small"></b-icon>
          </a>
        </div>
      </div>
    </div>


    <div v-if="showFilters" class="mt-15">
      <form @submit.prevent="onFilter">
        <div class="columns is-multiline">
          <div class="column is-3">
            <b-field>
              <form-select :label="$t('docflow.filters.state')" v-model="filters.state">
                <option value="active">{{ $t('workflow.states.active') }}</option>
                <option value="all">{{ $t('interface.all') }}</option>
                <option value="new">{{ $t('workflow.states.new') }}</option>
                <option value="pending_resolution">{{ $t('workflow.states.pending_resolution') }}</option>
                <option value="resolution_filled">{{ $t('workflow.states.resolution_filled') }}</option>
                <option value="resolution_approved">{{ $t('workflow.states.resolution_approved') }}</option>
                <option value="in_process">{{ $t('workflow.states.in_process') }}</option>
                <option value="rejected">{{ $t('workflow.states.rejected') }}</option>
                <option value="cancelled">{{ $t('workflow.states.cancelled') }}</option>
                <option value="pending_closing">{{ $t('workflow.states.pending_closing') }}</option>
                <option value="closed">{{ $t('workflow.states.closed') }}</option>
              </form-select>
            </b-field>
          </div>
          <div class="column is-3">
            <b-field>
              <form-select :label="`${$t('docflow.fields.delivery_type')}`" v-model="filters.delivery_type">
                <option value="all">{{ $t('interface.all') }}</option>
                <option v-for="type in deliveryTypes" :value="type.id">{{ type.name }}</option>
              </form-select>
            </b-field>
          </div>
          <div class="column is-3">
            <b-field>
              <form-select :label="`${$t('docflow.fields.register_book')}`" v-model="filters.register_book">
                <option value="all">{{ $t('interface.all') }}</option>
                <option v-for="registerBook in registerBooks" :value="registerBook.id">{{ registerBook.name }}</option>
              </form-select>
            </b-field>
          </div>
          <div class="column is-3">
            <b-field>
              <form-select :label="`${$t('docflow.fields.type')}`" v-model="filters.type">
                <option value="all">{{ $t('interface.all') }}</option>
                <option v-for="type in types" :value="type.id">{{ type.name }}</option>
              </form-select>
            </b-field>
          </div>
          <div class="column is-3">
            <b-field>
              <form-datepicker :label="$t('docflow.filters.created_date_from')"
                               v-model="filters.created_date_from">
              </form-datepicker>
            </b-field>
          </div>
          <div class="column is-3">
            <b-field>
              <form-datepicker :label="$t('docflow.filters.created_date_to')"
                               v-model="filters.created_date_to">
              </form-datepicker>
            </b-field>
          </div>
          <div class="column is-3">
            <b-field>
              <form-datepicker :label="$t('docflow.filters.term_date_from')"
                               v-model="filters.term_date_from">
              </form-datepicker>
            </b-field>
          </div>
          <div class="column is-3">
            <b-field>
              <form-datepicker :label="$t('docflow.filters.term_date_to')"
                               v-model="filters.term_date_to">
              </form-datepicker>
            </b-field>
          </div>
          <div class="column is-12">
            <b-checkbox class="mr-15" v-model="filters.is_expired">{{ $t('docflow.filters.is_expired') }}</b-checkbox>
            <b-checkbox class="mr-15" v-model="filters.is_informational">{{ $t('docflow.filters.is_informational') }}</b-checkbox>
            <b-checkbox v-model="filters.is_classified">{{ $t('docflow.filters.is_classified') }}</b-checkbox>
          </div>
          <div class="column is-12">
            <div class="field is-grouped">
              <div class="control">
                <button type="submit" class="button is-primary">{{ $t('interface.apply') }}</button>
              </div>
              <div class="control">
                <button type="button" class="button is-danger" @click="resetFilters">
                  <b-icon icon="filter-remove-outline" size="is-small"></b-icon>
                  <span>{{ $t('interface.clearFilters') }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>

    <b-table class="mt-10"
             :data="data"
             :loading="loading"

             paginated
             backend-pagination
             :total="total"
             :per-page="perPage"
             :current-page.sync="filters.page"
             @page-change="onPageChange"

             backend-sorting
             default-sort-direction="desc"
             :default-sort="[sortField, sortOrder]"
             @sort="onSort">
      <template slot-scope="props">
        <b-table-column :label="$t('docflow.fields.registration_number')" width="100" field="registration_number"
                        sortable>
          <a @click.prevent="detail(props.row.id)" style="position: relative">
            <span class="unread-indicator" v-if="!props.row.is_viewed"></span>
            <span>{{ props.row.registration_number }}</span>
            <b-tooltip :label="$t('docflow.informational')"
                       type="is-light">
              <b-icon v-if="props.row.is_informational"
                      icon="information-outline"
                      size="is-small"
                      class="has-text-grey-light mr-0 ml-5">
                      </b-icon>
                  </b-tooltip>
          </a>
        </b-table-column>
        <b-table-column field="registered_date" sortable :label="$t('docflow.fields.registered_date')">
          <template v-if="props.row.registered_date">
            {{ props.row.registered_date | date }}
          </template>
          <template v-else>&ndash;</template>
        </b-table-column>
        <b-table-column :label="$t('docflow.fields.type')">
          {{ props.row.type__name }}
        </b-table-column>
        <b-table-column :label="$t('docflow.fields.description')">
          <b-tooltip :label="props.row.description" class="cursor-info"
                     type="is-dark" position="is-right" multilined>
            {{ props.row.description | shortDescription }}
          </b-tooltip>
        </b-table-column>
        <b-table-column :label="$t('documents.fields.state')" field="state" sortable>
          {{ $t(`workflow.states.${props.row.state}`) }}
        </b-table-column>
        <b-table-column :label="$t('documents.fields.term')" width="100" field="term_date" sortable>
          <template v-if="props.row.term_date">
            <span :class="{expired: props.row.is_expired}">
              {{ props.row.term_date | date }}
            </span>
          </template>
        </b-table-column>
        <b-table-column :label="$t('documents.fields.maintainer')" width="180">
          {{ props.row.resolution_maintainer__initials }}
          <b-icon v-if="props.row.is_resolution_maintainer"
                  icon="account-hard-hat"
                  size="is-small"
                  class="has-text-grey-light mr-0 ml-5">
          </b-icon>
        </b-table-column>
        <b-table-column :label="$t('docflow.fields.sender')" width="250">
          {{ props.row.sender ? props.row.sender.name : '&ndash;' }}
        </b-table-column>
      </template>

      <template slot="bottom-left">
        <select class="paginationPager"
                :value="filters.page"
                @change="e => {
                  $emit('page-change', parseInt(e.target.value))
                }">
          <option v-for="page in pages" :value="page">{{ page }}</option>
        </select>
      </template>

      <template slot="empty">
        <section class="section">
          <div class="content is-fluid has-text-grey has-text-centered">
            <p>{{ $t('interface.nothingFound') }}
              <a class="is-dashed" @click.prevent="resetFilters">{{ $t('interface.resetFiltersAndSearch') }}</a>.
            </p>
          </div>
        </section>
      </template>
    </b-table>
  </div>
</template>

<script>
  import {mapState} from 'vuex'

  export default {
    props: {
      data: Array,
      loading: Boolean,
      total: Number,
      perPage: Number,
      sortField: String,
      sortOrder: String
    },
    data() {
      return {
        showFilters: false,
        filtersRelationsLoaded: false,
        filters: {}
      }
    },
    methods: {
      onPageChange(page) {
        this.$store.commit('Filters/SET', ['incomingFilters', this.filters, true]);
        this.$emit('page-change', page)
      },
      onSort(field, order) {
        this.$emit('sort', {field, order})
      },
      resetFilters() {
        this.$store.commit('Filters/RESET', 'incomingFilters');
        this.filters = {...this.incomingFilters};
        this.showFilters = false
        this.$emit('reset-filters')
      },
      onSearch() {
        this.$store.commit('Filters/SET', ['incomingFilters', this.filters]);
        this.$emit('search')
      },
      onFilter() {
        this.$store.commit('Filters/SET', ['incomingFilters', this.filters]);
        this.$emit('filter')
      },
      detail(id) {
        this.$router.push({
          name: 'docflowIncomingDetail',
          params: {documentId: id}
        })
      },
    },
    mounted() {
      this.filters = {...this.incomingFilters};
    },
    computed: {
      ...mapState('Docflow', ['types', 'deliveryTypes', 'registerBooks']),
      ...mapState('Filters', ['incomingFilters']),
      pages() {
        let quotient = Math.floor(this.total / this.perPage)
        let reminder = this.total % this.perPage;
        return reminder === 0 ? quotient : quotient + 1;
      }
    },
    watch: {
      showFilters(enabled) {
        if (enabled && !this.filtersRelationsLoaded) {
          this.$store.dispatch('Docflow/getRelations', {category: 'incoming', documentId: null})
          this.filtersRelationsLoaded = true
        }
      }
    }
  }
</script>
